import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useState, useEffect } from 'react';
import profile from 'src/store/profile';
import api from 'src/utils/api';
import { useNavigate } from 'react-router-dom';
import {
  Alert, 
  Card,
  Table,
  Stack,
  Paper,
  Avatar,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Link,
  Box
} from '@mui/material';
import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';
import { UserListHead, UserListToolbar } from '../../sections/@dashboard/user';

const TABLE_HEAD = [
  { id: 'name', label: 'Назва', alignRight: false },
  { id: 'company', label: 'Курс', alignRight: false },
  { id: 'role', label: 'Опис', alignRight: false },
  { id: 'isVerified', label: 'Активна', alignRight: false },
  { id: '' },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(array, (_user) => _user.name.toLowerCase().indexOf(query.toLowerCase()) !== -1);
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function GroupPage() {
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('name');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`sgroup-api?expand=course&filter[teacher_id]=${profile.data.id}`)
      .then((result) => setGroups(result))
      .catch(() => setGroups([]));
  }, []);

  const handleOpenMenu = (event) => setOpen(event.currentTarget);
  const handleCloseMenu = () => setOpen(null);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(groups.map((n) => n.name));
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };
  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - groups.length) : 0;
  const filteredUsers = applySortFilter(groups, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet><title>Студенти | Lala English</title></Helmet>

      <Alert severity="error" sx={{ mb: 5, pl: 3 }}>Перелік груп студентів, клікайте на назву групи для перегляду студентів.</Alert>
      <Container>
        <Card>
          <UserListToolbar numSelected={selected.length} filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={groups.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { 
                      id, 
                      name, 
                      info, 
                      course, 
                      is_substitution: isSubstitution, 
                      substitution_date: substitutionDate 
                    } = row;
                    
                    const selectedUser = selected.indexOf(name) !== -1;
                    
                    // Створюємо унікальний ключ для React
                    const rowKey = isSubstitution ? `sub-${id}-${substitutionDate}` : id;

                    return (
                      <TableRow hover key={rowKey} tabIndex={-1} role="checkbox" selected={selectedUser}>
                        <TableCell padding="checkbox" />
                        
                        <TableCell component="th" scope="row" padding="none">
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar alt={name} src={'/assets/images/avatars/avatar_1.jpg'} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                              <Link
                                component="button"
                                variant="subtitle2"
                                color="#333"
                                sx={{ textAlign: 'left' }}
                                onClick={() => navigate('/teacher/user', { state: row })}
                              >
                                {name}
                              </Link>
                              {isSubstitution && (
                                <Typography variant="caption" sx={{ mt: 0.5, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'warning.lighter', color: 'warning.darker' }}>
                                  Заміна ({substitutionDate})
                                </Typography>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        
                        <TableCell align="left">{course?.name}{course?.level ? ' Classic' : ' Base'}</TableCell>
                        <TableCell align="left">{info}</TableCell>
                        <TableCell align="left">Так</TableCell>
                        
                        <TableCell align="right">
                          <IconButton size="large" color="inherit" onClick={handleOpenMenu}>
                            <Iconify icon={'eva:more-vertical-fill'} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && <TableRow style={{ height: 53 * emptyRows }}><TableCell colSpan={6} /></TableRow>}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>Not found</Typography>
                          <Typography variant="body2">
                            No results found for &nbsp;<strong>&quot;{filterName}&quot;</strong>.<br /> Try checking for typos or using complete words.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={groups.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>

      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { p: 1, width: 140, '& .MuiMenuItem-root': { px: 1, typography: 'body2', borderRadius: 0.75 } } }}
      >
        <MenuItem><Iconify icon={'eva:edit-fill'} sx={{ mr: 2 }} />Edit</MenuItem>
        <MenuItem sx={{ color: 'error.main' }}><Iconify icon={'eva:trash-2-outline'} sx={{ mr: 2 }} />Delete</MenuItem>
      </Popover>
    </>
  );
}